import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputBase } from '@mui/material';
import React from 'react';
import { useCommonWebTranslation } from '../../hooks';
/**
 * Search input component that triggers a search callback on user query submission,
 * styled responsively for both small and large devices.
 *
 * @param {SearchProps} root0 - Component props.
 * @param {string} root0.searchLabel - Label used as placeholder and ARIA label for the search input.
 * @param {(query: string, onClose: () => void) => void} root0.onSearch - Callback triggered with the search query.
 * @param {() => void} root0.onSearchClose - Callback triggered when the search input is closed or blurred.
 * @param {boolean} root0.smallDevice - Whether the component is rendered on a small device, affecting layout.
 *
 * @returns {JSX.Element} A styled search input bar with a query input field and action icons.
 */
export const Search = ({ searchLabel, onSearch, onSearchClose, smallDevice }) => {
    const { t } = useCommonWebTranslation();
    if (searchLabel == null) {
        searchLabel = t('common.search');
    }
    const [query, setQuery] = React.useState('');
    const handleSearch = () => {
        if (query && onSearch) {
            onSearch(query, onSearchClose);
        }
    };
    return (React.createElement(Box, { sx: {
            display: 'flex',
            minHeight: 48,
            alignItems: 'center',
            width: smallDevice ? '100%' : 344,
            flexGrow: smallDevice ? 1 : undefined,
            pl: smallDevice ? 1 : 2,
            pr: smallDevice ? 1 : 2,
        } },
        React.createElement(Box, { sx: {
                display: 'flex',
                width: '100%',
                borderRadius: 1,
                bgcolor: 'background.paper',
                color: 'text.primary',
            } },
            React.createElement(IconButton, { size: 'small', sx: { m: '2px 8px 2px 8px' }, onClick: handleSearch, "data-test": 'search-button' },
                React.createElement(SearchIcon, null)),
            React.createElement(InputBase, { autoFocus: true, fullWidth: true, placeholder: searchLabel + '…', inputProps: { 'aria-label': searchLabel }, value: query, onChange: (event) => setQuery(event.target.value), onKeyDown: (event) => {
                    if (event.key === 'Enter') {
                        handleSearch();
                    }
                }, onBlur: () => {
                    setQuery('');
                    onSearchClose();
                }, "data-test": 'search-input' }),
            React.createElement(IconButton, { size: 'small', sx: { m: '2px 8px 2px 8px' }, onClick: onSearchClose, "data-test": 'close-search-button' },
                React.createElement(CloseIcon, null)))));
};
